import {
  ContextMenuData,
  ContextMenuEntities,
  ContextMenuListener,
  GetContextMenuOptionsArguments,
} from '@ardoq/context-menu';
import {
  getScenarioContextMenuOptionsForView,
  type GetScenarioContextMenuOptions,
} from 'scope/scenarioMenuOptions';
import {
  COMPONENT_ID_SELECTOR,
  REFERENCE_ID_SELECTOR,
  TAG_ID_ATTRIBUTE,
  TAG_ID_SELECTOR,
  WORKSPACE_ID_SELECTOR,
} from 'consts';
import {
  REFERENCE_ID_ATTRIBUTE,
  COMPONENT_ID_ATTRIBUTE,
  WORKSPACE_ID_ATTRIBUTE,
} from '@ardoq/global-consts';
import {
  getCommonMenuData,
  type ContextEntityTargets,
} from 'contextMenus/getCommonMenuData';
import { getTagMenu } from 'contextMenus/tagMenu';
import { toReferenceComponents } from 'contextMenus/referenceComponentsInterface';
import { type GlobalContextMenuAppState } from 'contextMenus/globalContextMenuAppState$';
import { type WorkspaceMenuState } from 'contextMenus/workspaceMenu';
import {
  isComponent,
  isReference,
  isWorkspace,
  ViewIds,
} from '@ardoq/api-types';

const EMPTY_ENTITIES: ContextMenuEntities = {
  componentsById: {},
  referencesById: {},
  workspacesById: {},
};

const findElement = (selector: string, target: Element) => {
  const element = target.closest(selector);
  return element instanceof HTMLElement || element instanceof SVGElement
    ? element
    : null;
};

const getTargets = (
  target: HTMLElement | SVGElement,
  { componentsById, referencesById, workspacesById }: ContextMenuEntities,
  workspaceMenuState: WorkspaceMenuState
): (ContextEntityTargets & { tagId?: string }) | null => {
  const workspaceElement = findElement(WORKSPACE_ID_SELECTOR, target);
  const workspaceId = workspaceElement?.getAttribute(WORKSPACE_ID_ATTRIBUTE);

  if (workspaceId) {
    const workspaces = [workspacesById[workspaceId]].filter(isWorkspace);
    return { workspaces, workspaceMenuState };
  }

  const tagElement = findElement(TAG_ID_SELECTOR, target);
  const tagId = tagElement?.getAttribute(TAG_ID_ATTRIBUTE);

  if (tagId) {
    return { tagId };
  }

  const referenceElement = findElement(REFERENCE_ID_SELECTOR, target);
  const referenceIds = referenceElement
    ?.getAttribute(REFERENCE_ID_ATTRIBUTE)
    ?.split(',');

  if (referenceIds) {
    const references = referenceIds
      .map(referenceId => referencesById[referenceId])
      .filter(isReference);
    return {
      referenceComponents: toReferenceComponents(references, componentsById),
    };
  }

  const componentElement = findElement(COMPONENT_ID_SELECTOR, target);
  const componentId = componentElement?.getAttribute(COMPONENT_ID_ATTRIBUTE);

  if (componentId) {
    const components = [componentsById[componentId]].filter(isComponent);
    return { components };
  }

  return null;
};

export const getPagesScenarioContextMenuOptions = (
  isViewpointMode: boolean,
  {
    i18nLocale,
    permissionContext,
    featureContext,
    currentContext,
  }: GlobalContextMenuAppState
): GetScenarioContextMenuOptions =>
  getScenarioContextMenuOptionsForView({
    isViewpointMode,
    viewId: ViewIds.PAGESVIEW,
    featureContext,
    permissionContext,
    context: currentContext,
    // Collapsing rules are a traversal concept; the pages view is workspace-mode only.
    hasCollapsingRuleApplied: false,
    i18nLocale,
  });

const getContextMenuDataFromEvent = (
  ctxMenuArguments: GetContextMenuOptionsArguments,
  appState: GlobalContextMenuAppState
): ContextMenuData => {
  const { target, entities = EMPTY_ENTITIES } = ctxMenuArguments;

  const targets = target
    ? getTargets(target, entities, appState.workspaceMenuState)
    : null;

  if (!targets) {
    return { options: null };
  }

  const { tagId, ...contextTargets } = targets;
  if (tagId) {
    return getTagMenu({ tagId, ...ctxMenuArguments });
  }

  return getCommonMenuData(
    ctxMenuArguments,
    {
      ...contextTargets,
      workspaceAndModel: appState.workspaceAndModel,
      componentsById: entities.componentsById,
      referenceCreation: appState.referenceCreation,
    },
    appState.i18nLocale,
    appState.permissionContext,
    getPagesScenarioContextMenuOptions(
      ctxMenuArguments.isViewpointMode,
      appState
    )
  );
};

export const pagesContextMenuName = 'pages-context-menu';

export const createPagesContextMenu = (
  appState: GlobalContextMenuAppState
): ContextMenuListener => ({
  getContextMenuData: ctxMenuArguments =>
    getContextMenuDataFromEvent(ctxMenuArguments, appState),
});
